import { compose, setPropTypes, withProps } from 'recompose';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import SettingsHeaderDropdown from './HeaderDropdown';
import { ShopAwareCommission } from '../../../auth/ShopAware';
import { RoleAwareShop } from '../../../auth/roleAware';
import {
  activeShopRewardsEnabled,
  activeShopWaitingListEnabled,
  getBrandId,
  getActiveBrandId,
  brandRewardsEnabled,
  getIsCommissionBarber,
  activeShop,
  getActiveType,
  getIsIndyBarber,
} from '../../../auth/selectors';
import AddBarberPrefix from '../../../common/RoleAwareLink';
import {
  canUserViewBankAccounts,
  canUserViewShopDetails,
  canUserViewBarberSettings,
  canUserSetWaitingList,
  canUserViewTaxes,
  canUserViewDiscounts,
  canUserViewPromos,
  canUserViewWaitingList,
  canUseCommissionReporting,
  isBrandOrSingleShopBrand,
  canUseIntegration,
  canUseChairRental,
  canUserViewBarbers,
  canUserViewAutoPayoutSettings,
} from '../../../common/helpers/users';
import withPermissions from '../../../auth/utils/withPermissions';
import { shouldOpenPaymentProcessingDashboard } from '../selectors';
import { DROPDOWNS } from './constants';
import { wasPreviouslyInteracted } from '../../../../modules/infra/persistence/interactionStateUtils';
import { USER_SETTINGS_SHOW_KEY } from '../../../../modules/shared/auth/two-fa/user-settings/user-settings-form';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../../modules/infra/feature-flags';

const getActiveDropdown = (settingsDropdownItems, paymentsDropdownItems, currentLocationPath) => {
  if (currentLocationPath &&
    paymentsDropdownItems.some(paymentsDropdownItem => currentLocationPath.endsWith(paymentsDropdownItem.to))
  ) {
    return DROPDOWNS.PAYMENTS;
  }
  return DROPDOWNS.SETTINGS;
};

const getBrandDropdownProps = (props) => {
  const settingsItems = [
    {
      label: 'Brand Settings',
      to: '/settings/details',
      component: AddBarberPrefix,
    },
    {
      label: 'User settings',
      to: '/settings/user',
      isNew: !wasPreviouslyInteracted(USER_SETTINGS_SHOW_KEY),
      displayIf: props['2FaEnable'],
    }, {
      label: 'Change password',
      to: '/settings/change-password',
      component: AddBarberPrefix,
    }, {
      label: 'Integrations',
      to: '/settings/integrations',
      displayIf: props.canVisitIntegrationPage,
      component: AddBarberPrefix,
    },
  ];

  let paymentItems = [
    {
      label: 'Subscription',
      to: '/settings/payment-card',
      displayIf: canUserViewBankAccounts(props.permissions),
      component: AddBarberPrefix,
    }, {
      label: 'Taxes',
      to: '/settings/taxes',
      component: AddBarberPrefix,
      displayIf: canUserViewTaxes(props.permissions),
    },
  ];

  if (!paymentItems.some(item => item.displayIf)) {
    paymentItems = [];
  }

  const activeDropdown = getActiveDropdown(settingsItems, paymentItems, props.location.pathname);

  return { settingsItems, paymentItems, activeDropdown };
};

const getShopOrBarberDropdownProps = (props) => {
  const settingsItems = [
    {
      label: 'Shop details',
      to: '/settings',
      displayIf: canUserViewShopDetails(props.permissions),
      wrapper: RoleAwareShop,
    },
    {
      label: 'User settings',
      to: '/settings/user',
      isNew: !wasPreviouslyInteracted(USER_SETTINGS_SHOW_KEY),
      displayIf: props['2FaEnable'],
      component: AddBarberPrefix,
    }, {
      label: 'Barber settings',
      to: '/settings/barber',
      displayIf: canUserViewBarberSettings(props.permissions),
      wrapper: RoleAwareShop,
    }, {
      label: 'Commission/Tip Splits',
      to: '/settings/commission',
      displayIf: props.commissionReportingEnabled && canUserViewBankAccounts(props.permissions),
      wrapper: ShopAwareCommission,
    }, {
      label: 'Change password',
      to: '/settings/change-password',
      displayIf: !props.brandId,
      component: AddBarberPrefix,
    }, {
      label: 'Discounts',
      to: '/settings/discounts',
      displayIf: canUserViewDiscounts(props.permissions) && !props.isCommissionBarber,
      component: AddBarberPrefix,
    }, {
      label: 'Loyalty Program',
      to: '/settings/loyalty-program',
      displayIf: canUserViewPromos(props.permissions) && props.rewardsEnabled && !props.isCommissionBarber,
      component: AddBarberPrefix,
    }, {
      label: 'Waiting List',
      to: '/settings/waiting-list',
      displayIf: (
        canUserViewWaitingList(props.permissions)
        && props.waitingListEnabled
        && canUserSetWaitingList(props.user, props.shopKind)
      ),
      component: AddBarberPrefix,
    }, {
      label: 'Promo Codes',
      to: '/settings/promo',
      displayIf: canUserViewPromos(props.permissions) && !props.isCommissionBarber,
      component: AddBarberPrefix,
    }, {
      label: 'Integrations',
      to: '/settings/integrations',
      displayIf: props.canVisitIntegrationPage,
      component: AddBarberPrefix,
    },
  ];

  let paymentItems = [
    {
      label: 'Auto Payouts',
      to: '/settings/auto-payout',
      displayIf: canUserViewAutoPayoutSettings(props.permissions),
      component: AddBarberPrefix,
    },
    {
      label: 'Payment Processing',
      to: '/settings/bank-account',
      onlyActiveOnIndex: false,
      displayIf: canUserViewBankAccounts(props.permissions) && !props.shouldOpenPaymentProcessingDashboard,
      component: AddBarberPrefix,
    }, {
      label: 'Payment Processing',
      to: '/settings/payment-processing',
      onlyActiveOnIndex: false,
      displayIf: canUserViewBankAccounts(props.permissions) && props.shouldOpenPaymentProcessingDashboard,
      component: AddBarberPrefix,
    }, {
      label: 'Rent Collect',
      to: '/barbers/chair-rental',
      displayIf: props.canVisitRentCollectPage,
      component: AddBarberPrefix,
    }, {
      label: 'Taxes',
      to: '/settings/taxes',
      displayIf: canUserViewTaxes(props.permissions) && !props.isCommissionBarber,
      component: AddBarberPrefix,
    },
    {
      label: 'Subscription',
      to: '/settings/payment-card',
      displayIf: props.isIndyBarber,
      component: AddBarberPrefix,
    },
  ];

  if (!paymentItems.some(item => item.displayIf)) {
    paymentItems = [];
  }

  const activeDropdown = getActiveDropdown(settingsItems, paymentItems, props.location.pathname);

  return { settingsItems, paymentItems, activeDropdown };
};

export default compose(
  setPropTypes({
    title: PropTypes.string.isRequired,
  }),
  withPermissions,
  withInjectFeatureFlagsToProps([flagNames['2FA_ENABLE'], flagNames.HYBRID_EXPOSE_RENT_COLLECT]),
  connect((state, ownProps) => ({
    user: state.auth.user,
    shopKind: state.auth.shopKind,
    rewardsEnabled: activeShopRewardsEnabled(state),
    brandRewardsEnabled: brandRewardsEnabled(state),
    waitingListEnabled: activeShopWaitingListEnabled(state),
    commissionReportingEnabled: canUseCommissionReporting(state),
    brandId: getBrandId(state),
    activeBrandId: getActiveBrandId(state),
    isCommissionBarber: getIsCommissionBarber(state),
    isIndyBarber: getIsIndyBarber(state),
    isShopViewActive: getActiveType(state) === 'shop',
    shouldOpenPaymentProcessingDashboard: shouldOpenPaymentProcessingDashboard(state),
    canVisitIntegrationPage: canUseIntegration(state) && isBrandOrSingleShopBrand(state),
    canVisitRentCollectPage: getActiveType(state) === 'shop'
      && canUserViewBarbers(state.auth.permissions)
      && canUseChairRental(activeShop(state), ownProps[flagNames.HYBRID_EXPOSE_RENT_COLLECT]),
  })),
  withRouter,
  withProps(props => (props.activeBrandId ? getBrandDropdownProps(props) : getShopOrBarberDropdownProps(props))),
)(SettingsHeaderDropdown);
